import { useFormContext } from "@/lib/form/form-context"
import { DropdownParamSchema } from "@/lib/form/schema"
import { FormParamStatus, WidgetBaseProps } from "@/lib/form/types"
import { toPathString, useFieldChange, useWatch } from "@/lib/form/form"
import { cn, createUuid } from "@/lib/utils"
import Select, { SelectOption } from "@/components/select"
import { Icon } from "@/icons/icon"
import { ChangeEvent, useEffect, useState } from "react"
import { toPath } from "lodash"
import { ParamFormData } from "@/packaging/lib/form"

export interface LeakageWidgetProps
  extends WidgetBaseProps<DropdownParamSchema> {
  className?: string
}

export interface LeakageInput {
  id: string
  count?: number
  type: string
}

export default function LeakageWidget(props: LeakageWidgetProps) {
  const MAX_ALLOWED_LEAKAGE = 0.25

  const { className, schema, path } = props
  const { form } = useFormContext()
  const sectionPath = useSectionPath()
  const totalLeaked = useWatch({
    form,
    name: `${sectionPath}.parameters[1]`,
    defaultValue: { value: 0 },
    immediate: true,
  })
  const totalPackets = useWatch({
    form,
    name: `${sectionPath}.parameters[0]`,
    defaultValue: { value: 0 },
    immediate: true,
  })
  const value = useWatch({
    form: form,
    name: path,
    deep: 1,
    immediate: true,
  })
  const { inputs, add, remove, change } = useLeakageInputs()
  const avg = useAverageLeakage()

  function useLeakageInputs() {
    const [inputs, setInputs] = useState<LeakageInput[]>([])

    function reset() {
      setInputs([])
      updateForm([])
    }

    function remove(id: string): LeakageInput[] {
      const newInputs = inputs.filter((input) => input.id !== id)
      setInputs(newInputs)
      updateForm(newInputs)

      return newInputs
    }

    function add(): LeakageInput[] {
      const newInputs = [
        ...inputs,
        { id: createUuid(), type: "" },
      ]
      setInputs(newInputs)
      updateForm(newInputs)

      return newInputs
    }

    function change(id: string, value: LeakageInput): LeakageInput[] {
      const newInputs = inputs.map((input) => {
        if (input.id === id) {
          return value
        }

        return input
      })
      setInputs(newInputs)
      updateForm(newInputs)

      return newInputs
    }

    function updateForm(inputs: LeakageInput[]) {
      form.setValue(
        path,
        {
          id: schema.id,
          value: inputs,
          status: FormParamStatus.DRAFT,
          recordedAt: new Date().toISOString(),
        },
        {
          shouldTouch: true,
          shouldValidate: true,
          shouldDirty: true,
        }
      )
    }

    useFieldChange({
      form,
      name: `${sectionPath}.parameters[1]`,
      options: {
        deep: true,
      },
      async handler(__e, ctx) {
        const payload = ctx.payload as ParamFormData | undefined
        if (!payload) {
          return
        }

        const count = Number(payload?.value || 0)
        if (isNaN(count) || !count) {
          reset()
          void form.runAllValidators()

          return
        }
      },
    })

    useEffect(() => {
      const count = Number(totalLeaked?.value || 0)
      if (isNaN(count) || !count) {
        return
      }

      if (value?.value?.length) {
        setInputs(value.value)
        void form.runAllValidators()

        return
      }

      const newInputs: LeakageInput[] = [
        { id: createUuid(), type: "" },
      ]
      setInputs(newInputs)
      void form.runAllValidators()
    }, [totalLeaked, value])

    return {
      inputs,
      remove,
      add,
      change,
      reset,
      updateForm,
    }
  }

  function useSectionPath() {
    const [sectionPath, setSectionPath] = useState("")

    useEffect(() => {
      const parts = toPath(path)
      setSectionPath(toPathString(parts.slice(0, 4)))
    }, [path, schema])

    return sectionPath
  }

  function showFlag() {
    return Number(avg) > MAX_ALLOWED_LEAKAGE
  }

  function handleChange(id: string, data: LeakageData) {
    change(id, {
      id,
      count: data.count,
      type: data.type,
    })
  }

  function useAverageLeakage() {
    const [avg, setAvg] = useState("")

    useEffect(() => {
      const total = Number(totalPackets?.value)
      const leaked = Number(totalLeaked?.value)

      if (isNaN(total) || isNaN(leaked)) {
        setAvg("")

        return
      }

      setAvg(((leaked / total) * 100).toFixed(2))
    }, [totalLeaked, totalPackets])

    return avg
  }

  function handleLeakageAction(action: LeakageItemAction, id: string) {
    if (action === "add") {
      add()
    } else {
      remove(id)
    }
  }

  if (value?.status && value?.status !== FormParamStatus.DRAFT) {
    return (
      <div
        className={cn(
          "border border-input-border rounded-lg w-full text-sm font-normal overflow-hidden",
          className
        )}
      >
        <div className="bg-backdrop py-2 px-3 flex justify-between items-center">
          <div>
            <span className="font-semibold">{totalPackets?.value}</span>
            &nbsp;Packets Checked
          </div>

          <div className="flex items-center gap-3">
            <div className="flex gap-3 items-center h-6 bg-blue-fill border border-blue border-dashed px-2">
              <span className="text-tiny">Leakage</span>{" "}
              <span className="text-sm">{avg} %</span>
            </div>

            {showFlag() && <Icon name="RedFlag" className="h-4 w-auto" />}
          </div>
        </div>

        <div className="py-4 px-3">
          <div className="flex justify-between items-center gap-8">
            <div className="flex flex-col gap-1">
              <span className="text-label-text">Packets Leaked</span>
              <span className="text-base">{totalLeaked?.value}</span>
            </div>

            <div className="flex flex-col gap-1">
              {inputs.map((input) => (
                <div
                  key={input.id}
                  className="text-label-text text-tiny font-medium flex items-center gap-1.5"
                >
                  <div className="rounded-full bg-backdrop text-button-black size-5 flex items-center justify-center">
                    {input.count}
                  </div>
                  <div>{input.type}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!inputs.length) {
    return false
  }

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {inputs.map((input, index) => (
        <LeakageItemInput
          key={input.id}
          id={input.id}
          action={index === inputs.length - 1 ? "add" : "delete"}
          value={input}
          options={schema.options}
          onClickAction={handleLeakageAction}
          onChange={handleChange}
        />
      ))}

      <hr className="w-full h-0 border-t border-l-border mt-2" />
    </div>
  )
}

type LeakageItemAction = "add" | "delete"

interface LeakageItemInputProps {
  id: string
  action: LeakageItemAction
  value: LeakageData
  options: SelectOption[]
  onChange?: (id: string, data: LeakageData) => void
  onClickAction?: (type: LeakageItemAction, id: string) => void
}

interface LeakageData {
  id: string
  count?: number
  type: string
}

function LeakageItemInput({
  id,
  action,
  value,
  options,
  onClickAction,
  onChange,
}: LeakageItemInputProps) {
  function handleClickAction() {
    if (onClickAction) {
      onClickAction(action, id)
    }
  }

  function handleChangeCount(e: ChangeEvent<HTMLInputElement>) {
    if (onChange) {
      onChange(id, {
        ...value,
        count: Number(e.target.value),
      })
    }
  }

  function handleChangeType(type: string) {
    if (onChange) {
      onChange(id, {
        ...value,
        type: type,
      })
    }
  }

  return (
    <div className="flex w-full h-12 rounded-sm border border-input-border shadow-xs text-sm font-medium">
      <input
        className="min-w-13 max-w-13 px-4 py-3 border-r border-input-border"
        type="text"
        inputMode="numeric"
        value={value.count}
        onChange={handleChangeCount}
      />
      <Select
        className="grow"
        selectClassName="border-none rounded-none"
        options={options}
        value={value.type}
        onChange={handleChangeType}
      />
      <button
        className="flex justify-center items-center min-w-13 max-w-13 border-l border-input-border hover:cursor-pointer"
        type="button"
        onClick={handleClickAction}
      >
        {action === "add" ? (
          <Icon name="Plus" className="text-blue h-5 w-auto" />
        ) : (
          <Icon name="Dash" className="text-blue h-6 w-auto" />
        )}
      </button>
    </div>
  )
}
